import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lightbulb, ThumbsUp, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { STATUS_META } from '../../pages/SuggestionsPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SuggestionsAdminPanel = () => {
  const [data, setData] = useState({ items: [], counts: {} });
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/suggestions`, { withCredentials: true });
      setData(res.data);
    } catch { toast.error('Erreur chargement suggestions'); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      const res = await axios.put(`${API}/admin/suggestions/${id}/status`, { status }, { withCredentials: true });
      setData((d) => ({ ...d, items: d.items.map((s) => (s.id === id ? { ...s, ...res.data } : s)) }));
      toast.success(`Statut : ${STATUS_META[status].label}`);
    } catch { toast.error('Erreur mise à jour'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette suggestion ?')) return;
    try {
      await axios.delete(`${API}/admin/suggestions/${id}`, { withCredentials: true });
      setData((d) => ({ ...d, items: d.items.filter((s) => s.id !== id) }));
    } catch { toast.error('Erreur suppression'); }
  };

  const items = filter === 'all' ? data.items : data.items.filter((s) => s.status === filter);

  return (
    <Card data-testid="admin-suggestions-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="font-heading flex items-center gap-2"><Lightbulb className="w-5 h-5 text-[#F59E0B]" /> Idées des utilisateurs ({data.items.length})</CardTitle>
        <div className="flex gap-2 flex-wrap">
          {['all', ...Object.keys(STATUS_META)].map((k) => (
            <button key={k} onClick={() => setFilter(k)} className={`text-xs px-3 py-1 rounded-full border ${filter === k ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'border-[#E5E7EB] text-[#4B5563]'}`} data-testid={`admin-suggestions-filter-${k}`}>
              {k === 'all' ? 'Toutes' : STATUS_META[k].label} {k !== 'all' && `(${data.counts[k] || 0})`}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 && <p className="text-sm text-[#6B7280] py-6 text-center" data-testid="admin-suggestions-empty">Aucune suggestion.</p>}
        {items.map((s) => (
          <div key={s.id} className="p-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] flex gap-4 flex-wrap md:flex-nowrap" data-testid={`admin-suggestion-${s.id}`}>
            <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-white border border-[#E5E7EB] text-[#1E3A8A] flex-shrink-0">
              <ThumbsUp className="w-4 h-4" />
              <span className="font-bold" data-testid={`admin-suggestion-votes-${s.id}`}>{s.votes}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-[#111827]">{s.title}</h4>
                <Badge className={STATUS_META[s.status]?.className}>{STATUS_META[s.status]?.label}</Badge>
              </div>
              <p className="text-sm text-[#4B5563] mt-1 whitespace-pre-line">{s.description}</p>
              <p className="text-xs text-[#9CA3AF] mt-2">{s.author_name} ({s.author_type}) · {s.author_email} · {s.created_at ? new Date(s.created_at).toLocaleDateString('fr-FR') : ''}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Select value={s.status} onValueChange={(v) => setStatus(s.id, v)}>
                <SelectTrigger className="w-[150px]" data-testid={`admin-suggestion-status-${s.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => remove(s.id)} data-testid={`admin-suggestion-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
